import { getSlateColorToken } from "@faire/retailer-shared/SlateMappings/getSlateColorToken";
import { ColorTokenKey } from "@faire/web-api/indigofair/data/ColorTokenKey";
import { IAvatar } from "@faire/web-api/indigofair/data/IAvatar";
import { IAvatarElement } from "@faire/web-api/indigofair/data/IAvatarElement";
import { Avatar } from "@faire/web/slate/Avatar";
import {
  AvatarBorderStyle,
  AvatarIconPosition,
  AvatarSize,
  DIMENSION_BY_SIZE,
} from "@faire/web/slate/Avatar/Avatar";
import React from "react";

import { SDUIImage } from "@faire/retailer/components/SDUI/__internal__/Image/SDUIImage";
import { SDUIOnClickHandler } from "@faire/retailer/components/SDUI/__internal__/SDUIOnClickHandler/SDUIOnClickHandler";
import {
  LoggingEventParams,
  SDUIComponentRenderBehaviour,
} from "@faire/retailer/components/SDUI/__internal__/types";

export const SDUIAvatarElementRenderer: React.FC<{
  className?: string;
  componentStyle?: React.CSSProperties;
  avatarElement: IAvatarElement;
  renderBehaviour?: SDUIComponentRenderBehaviour;
  loggingData: LoggingEventParams | undefined;
}> = ({
  className,
  componentStyle,
  avatarElement,
  renderBehaviour,
  loggingData,
}) => {
  const { avatar, on_click } = avatarElement ?? {};
  const { image_avatar, text_avatar, overlay_badge, style } = avatar ?? {};
  const { size = "S", border } = style ?? {};

  const slateWebAvatarSize = getSlateWebAvatarSize(size);
  const avatarIconData =
    overlay_badge?.image && overlay_badge.position
      ? {
          position: getSlateWebAvatarIconPosition(overlay_badge.position),
          icon: (
            <SDUIImage
              optimizeImageOptions={{
                width: DIMENSION_BY_SIZE[slateWebAvatarSize] / 2,
                height: DIMENSION_BY_SIZE[slateWebAvatarSize] / 2,
              }}
              responsiveImage={{ mobileAndAbove: overlay_badge.image }}
              renderBehaviour={renderBehaviour}
            />
          ),
        }
      : undefined;

  if (!text_avatar && !image_avatar) {
    return null;
  }

  return (
    <SDUIOnClickHandler
      onClickActions={on_click}
      loggingData={loggingData}
      logLegacyClickEvent
      className={className}
      componentStyle={componentStyle}
    >
      <Avatar
        size={slateWebAvatarSize}
        borderColor={getSlateColorToken(
          border?.border_color,
          ColorTokenKey.COLOR_TOKEN_KEY_UNKNOWN
        )}
        borderStyle={getSlateWebAvatarBorderStyle(border?.border_style)}
        userName={text_avatar}
        image={image_avatar}
        iconData={avatarIconData}
      />
    </SDUIOnClickHandler>
  );
};

// mobile uses S,M,L,etc. for avatar sizing, so the proto value needs to be converted from those to Slate web versions
export const getSlateWebAvatarSize = (
  size: keyof typeof IAvatar.AvatarSize | undefined
): AvatarSize => {
  switch (size) {
    case "XXS":
      return "xx-small";
    case "XS":
      return "x-small";
    case "S":
      return "small";
    case "M":
      return "medium";
    case "L":
      return "large";
    case "XL":
      return "x-large";
    default:
      return "medium";
  }
};

const getSlateWebAvatarIconPosition = (
  position: keyof typeof IAvatar.IOverlayBadge.Position | undefined
): AvatarIconPosition => {
  switch (position) {
    case "TOP_LEFT":
      return "topLeft";
    case "BOTTOM_LEFT":
      return "bottomLeft";
    case "TOP_RIGHT":
      return "topRight";
    case "BOTTOM_RIGHT":
    default:
      return "bottomRight";
  }
};

const getSlateWebAvatarBorderStyle = (
  borderStyle: keyof typeof IAvatar.IAvatarStyle.AvatarBorderStyle | undefined
): AvatarBorderStyle | undefined => {
  switch (borderStyle) {
    case "SLATE_OUTLINED":
      return "OUTLINED";
    case "THIN":
      return "THIN";
    default:
      return undefined;
  }
};
